/**
 * BookingRouteError - React Router `errorElement` for booking routes.
 * Renders a visible fallback when a route loader/action throws or a
 * descendant component crashes during rendering. Unlike a class-based
 * React error boundary, this reads the error via `useRouteError()` so
 * React Router's data-router wires it correctly.
 */

import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

export function BookingRouteError(): React.ReactElement {
  const error = useRouteError();

  // Only surface raw error text in development. In production this screen is
  // shown to end users on the public booker, where leaking exception messages
  // (stack-trace text, internal field names, library errors) can disclose
  // implementation details and confuse the booker.
  let devErrorMessage: string | null = null;
  if (import.meta.env.DEV) {
    if (isRouteErrorResponse(error)) {
      devErrorMessage = `${error.statusText}${error.data ? `: ${error.data}` : ''}`;
    } else if (error instanceof Error) {
      devErrorMessage = error.message;
    } else if (typeof error === 'string') {
      devErrorMessage = error;
    } else {
      devErrorMessage = 'Unknown error';
    }
  }

  return (
    <div
      role='alert'
      className='flex flex-col items-center justify-center py-12 px-6 text-center min-h-dvh'
    >
      <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-error-100)] mb-6'>
        <svg
          className='w-8 h-8 text-[var(--color-text-error-primary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          aria-hidden='true'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
          />
        </svg>
      </div>

      <h2 className='text-xl font-semibold text-fg-primary mb-2'>
        Something went wrong
      </h2>
      <p className='text-fg-secondary max-w-md mb-6'>
        We encountered an unexpected error while loading the booking page.
        Please try refreshing.
      </p>

      {devErrorMessage !== null && (
        <p className='text-sm text-fg-secondary italic'>{devErrorMessage}</p>
      )}
    </div>
  );
}
